// @flow
import React from "react";
import { useHistory } from "react-router-dom";
import glamorous from "glamorous";
import cx from "classnames";
import { connect } from "react-redux";
import consts from "common/consts";
import t from "common/i18n";
import { globalTheme, useMixin } from "@cld/component-theme";
import { bindStructuredActions } from "common/actionsBase";
import bindRootActions, { types as rootAts } from "root/actions";
import MenuButton from "root/components/MenuButton/MenuButton";
import LinkButton from "root/components/LinkButton/LinkButton";
import PoweredBy from "root/components/PoweredBy/PoweredBy";
import Button from "root/components/Button";
import Progress from "root/components/Progress/Progress";
import {
	selectIsMultipleEnabled,
	selectShowPoweredBy,
	selectIsInlineMode,
} from "root/selectors";
import {
	selectUploadQueueItemIds,
	selectQueueCounters,
	selectShowCompletedButton,
	selectShowUploadMoreButton,
	selectQueueProgress,
} from "../../selectors";
import ExpandedItem from "../ExpandedItem/ExpandedItem";
import SVG_ICONS from "~/assets/icons/icons";

type RootActions = { [string]: Function };

type OwnProps = {|
	showCompleted: Function,
|};

type Props = {
	...OwnProps,
	queueIds: string[],
	queueProgress: number,
	active: number,
	failed: number,
	done: number,
	isSimulated: boolean,
	isDone: boolean,
	rootActions: RootActions,
	showCompletedButton: boolean,
	showUploadMoreButton: boolean,
	showPoweredBy: boolean,
	isInline: boolean,
	isMultiple: boolean,
};

const GExpandedQueue = glamorous.div(cx(
	"ExpandedQueue",
	"relative left-0 right-0 bottom-0 top-0",
	"w-100 h-100",
	"flex flex-column",
),
{
	borderRadius: 6,
	boxShadow: "0 20px 30px rgba(0,0,0,.5)",
});

const GHeader = glamorous.div(cx(
	"ExpandedQueue--HEAD",
	"flex-none",
	"flex items-center justify-between",
	"bg-window bg-window-c",
	"bb bc-window-border",
), {
	height: 52,
	paddingLeft: 20,
	paddingRight: 20,
	boxShadow: "0 2px 4px 0 rgba(0,0,0,0.5)",
});

const GBody = glamorous.div(cx(
	"ExpandedQueue--BODY",
	"overflow-y-auto",
	"flex-grow flex-wrap",
	"flex content-start",
	"bg-source-bg bg-source-bg-c",
), {
	padding: 9,
});

const GFooter = glamorous.div((props: { showPoweredBy: boolean }) => cx(
	"ExpandedQueue--FOOTER",
	"flex items-center",
	"bg-window bg-window-c",
	"bt bc-window-border",
	{
		"justify-end": !props.showPoweredBy,
		"justify-between": props.showPoweredBy,
	},
), {
	height: "70px",
	padding: "9px 16px",
});

export const ExpandedQueue = (props: Props) => {
	const history = useHistory();

	const linkFontSize = useMixin(globalTheme.font.size.xxs);
	const linkIconSize = "xs";

	const title = props.isDone
		? t("queue.title")
		: (props.isSimulated ? t("queue.title_uploading") // only showing "loading" without a number
			: t("queue.title_uploading_with_counter", { num: props.active }));

	const retryFailedItem = (id) => props.rootActions[rootAts.SET_QUEUE_ITEMS_RETRY]({ ids: [id] });

	const abortItem = (id) => props.rootActions[rootAts.SET_QUEUE_ITEMS_ABORT]({ ids: [id] });

	const renderQueueItems = (ids) => {
		const isSingleItem = ids.length === 1;
		const cls = cx("self-start", {
			"w-50 w-25-ns": !isSingleItem,
			"w-100": isSingleItem,
		});

		return ids.map((id: string) => <ExpandedItem className={cls} key={`q_ex_item_${id}`}
													 id={id}
													 paddingBottom={isSingleItem ? "430px" : "100%"}
													 gutter={isSingleItem ? 0 : 17}
													 retryFailed={retryFailedItem}
													 abort={abortItem}/>);
	};

	const abortAllButton = props.active
		? <LinkButton text={t("queue.abort_all")}
					  className="mr3"
					  textBreakpoint="ns"
					  icon={SVG_ICONS.PAUSE}
					  dataTest="abort-all-btn"
					  onClick={() => props.rootActions[rootAts.SET_QUEUE_ITEMS_ABORT]()}
					  fontSize={linkFontSize}
					  iconSize={linkIconSize}/> : null;

	const retryAllButton = props.failed > 0
		? <LinkButton dataTest="retry-all-btn"
					  text={t("queue.retry_failed")}
					  textBreakpoint="ns"
					  className="mr3"
					  icon={SVG_ICONS.RETRY}
					  onClick={() => props.rootActions[rootAts.SET_QUEUE_ITEMS_RETRY]()}
					  textColorName="link"
					  fontSize={linkFontSize}
					  iconSize={linkIconSize}
					  tabIndex={0}/> : null;

	const showCompletedButton = props.done > 0 && props.showCompletedButton
		? <LinkButton dataTest="show-completed-btn"
					  text={t("queue.show_completed")}
					  textBreakpoint="ns"
					  className="mr3"
					  icon={SVG_ICONS.COMPLETE}
					  onClick={() => props.showCompleted()}
					  textColorName="link"
					  fontSize={linkFontSize}
					  iconSize={linkIconSize}
					  tabIndex={0}/> : null;

	const uploadMoreButton = props.isMultiple && props.showUploadMoreButton
		&& <LinkButton dataTest="upload-btn"
					   text={t("queue.upload_more")}
					   textBreakpoint="ns"
					   className="mr3"
					   icon={SVG_ICONS.UPLOAD_MORE}
					   fontSize={linkFontSize}
					   iconSize={linkIconSize}
					   tabIndex={0}
					   onClick={() => props.rootActions[rootAts.SET_WIDGET_VIEW_TYPE]({
						   type: consts.WIDGET_VIEW_TYPES.INITIAL,
						   router: { history },
					   })}/>;

	const doneButton = <Button dataTest="queue-done"
							   disabled={(!props.isDone || props.isInline)}
							   colorName="action" icon={SVG_ICONS.CHECKED}
							   text={t("queue.done")}
							   tabIndex={0}
							   onClick={() => props.rootActions[rootAts.DO_CLOSE_WIDGET]()}/>;

	return <GExpandedQueue data-test="expanded-upload-queue">
		<GHeader data-test="upload-queue-header">
			<glamorous.Div className="flex items-center fw5 f5" data-test="upload-queue-title">
				{!props.isDone
					? <Progress size={33} percentage={props.queueProgress} className="mr3"/> : null}
				{title}
			</glamorous.Div>
			{!props.isInline ? <glamorous.Div className="flex flex-nowrap items-center justify-end"
											  data-test="eq-close-mini-box">
				<MenuButton dataTest="minimize-dialog-icon"
							iconSize="sm"
							className="mr6"
							icon={SVG_ICONS.KEYBOARD_DOWN}
							onClick={() => props.rootActions[rootAts.SET_WIDGET_VIEW_TYPE]({ type: consts.WIDGET_VIEW_TYPES.MINI })}
							tabIndex={0}
				/>
			</glamorous.Div> : null}
		</GHeader>
		<GBody>
			{renderQueueItems(props.queueIds)}
		</GBody>
		<GFooter showPoweredBy={props.showPoweredBy}>
			{props.showPoweredBy ? <PoweredBy/> : null}
			<div className="flex items-center">
				{abortAllButton}
				{retryAllButton}
				{showCompletedButton}
				{uploadMoreButton}
				{doneButton}
			</div>
		</GFooter>
	</GExpandedQueue>;
};

export default connect<Props, OwnProps, _, _, _, _>(
	(state) => ({
		...selectQueueCounters(state),
		queueIds: selectUploadQueueItemIds(state),
		showCompletedButton: selectShowCompletedButton(state),
		showUploadMoreButton: selectShowUploadMoreButton(state),
		showPoweredBy: selectShowPoweredBy(state),
		queueProgress: selectQueueProgress(state),
		isInline: selectIsInlineMode(state),
		isMultiple: selectIsMultipleEnabled(state),
	}),
	bindStructuredActions({
		rootActions: bindRootActions,
	}),
)(ExpandedQueue);
